/**
 * E018-S007-T001: useAutomationUIState Hook
 *
 * Single Source of Truth for Automation UI State.
 * Combines ExecutionService state to provide a unified, consistent UI state.
 *
 * This hook eliminates state divergence between:
 * - Sidebar StatusBadge ("Ready" / "Running")
 * - ExecutionPanel controls
 */

import { useLiveData, useService } from '@toeverything/infra';
import { useMemo } from 'react';

import { ExecutionService, type ExecutionStatus } from '../services/execution';

// =============================================================================
// UI State Types
// =============================================================================

/**
 * Badge status for StatusBadge component
 * Maps to visual appearance and label
 */
export type BadgeStatus =
  | 'ready' // Gray - Ready to run
  | 'running' // Blue - Execution in progress
  | 'queued' // Yellow - Waiting for capacity
  | 'paused' // Yellow - Paused
  | 'completed' // Green - Completed
  | 'cancelled' // Gray - Cancelled by user
  | 'failed'; // Red - Failed

/**
 * Button state for Run button
 * Determines button label and enabled state
 */
export type ButtonState =
  | 'ready_to_run' // Show "Run (n)", enabled
  | 'running'; // Show progress, disabled

/**
 * Unified UI State returned by the hook
 */
export interface AutomationUIState {
  // Badge State
  badgeStatus: BadgeStatus;
  badgeLabel: string;

  // Button State
  buttonState: ButtonState;
  runButtonLabel: string;

  // Capability flags
  canRun: boolean;
  canStop: boolean;

  // Derived values
  isRunning: boolean;
  rowCount: number;

  // Raw states (for advanced use)
  executionStatus: ExecutionStatus;
}

// =============================================================================
// Badge Label Mapping
// =============================================================================

const BADGE_LABELS: Record<BadgeStatus, string> = {
  ready: 'Ready',
  running: 'Running',
  queued: 'Waiting',
  paused: 'Paused',
  completed: 'Completed',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * useAutomationUIState - Single Source of Truth for UI State
 *
 * @param blockId - The automation block ID
 * @param rowCount - Number of rows to process (optional, for button label)
 * @returns Unified UI state
 */
export function useAutomationUIState(
  blockId: string | null,
  rowCount: number = 0
): AutomationUIState {
  const executionService = useService(ExecutionService);

  // Get execution state from service
  const executionStateLive$ = blockId
    ? executionService.getState$(blockId)
    : null;
  const executionState = useLiveData(executionStateLive$);

  return useMemo(() => {
    const executionStatus: ExecutionStatus = executionState?.status ?? 'idle';

    // Execution flags
    const isRunning = executionStatus === 'running';
    const isQueued = executionStatus === 'queued';
    const isCompleted = executionStatus === 'completed';
    const isCancelled = executionStatus === 'cancelled';
    const isFailed = executionStatus === 'failed';

    // Determine badge status (priority order)
    let badgeStatus: BadgeStatus;
    if (isQueued) {
      badgeStatus = 'queued';
    } else if (isRunning) {
      badgeStatus = 'running';
    } else if (isFailed) {
      badgeStatus = 'failed';
    } else if (isCancelled) {
      badgeStatus = 'cancelled';
    } else if (isCompleted) {
      badgeStatus = 'completed';
    } else {
      badgeStatus = 'ready';
    }

    // Determine button state
    const isActive = isRunning || isQueued;
    const buttonState: ButtonState = isActive ? 'running' : 'ready_to_run';

    // Run button label
    const runButtonLabel = isQueued
      ? 'Waiting...'
      : buttonState === 'running'
        ? 'Running...'
        : `Run (${rowCount})`;

    // Capability flags
    const canRun = !isActive && rowCount > 0;
    const canStop = isActive;

    return {
      // Badge
      badgeStatus,
      badgeLabel: BADGE_LABELS[badgeStatus],

      // Button
      buttonState,
      runButtonLabel,

      // Capabilities
      canRun,
      canStop,

      // Derived
      isRunning: isActive,
      rowCount,

      // Raw
      executionStatus,
    };
  }, [executionState, rowCount]);
}
